<script lang="ts">
import { FieldType } from '@mimir/common/mdf/fieldDefinition';
import type { Alternative, MdfFieldEntity } from '@mimir/common/mdf/fieldDefinition';
import type { UserInfoDto } from '@mimir/common/dto/userDto';
import type { PropType } from '@mimir/web/compat/vueCompat';
import { defineComponent } from '@mimir/web/compat/vueCompat';
import { getCommonChipStyle } from '../mdf-client';
import { VBtn, VChip, VCol, VHover, VIcon, VRow } from 'vuetify/lib';
import isEmpty from 'lodash/isEmpty';
import { assert } from '@mimir/common/util/invariant';

export default defineComponent({
  
  name: 'ValueField',
  components: {
    VBtn,
    VChip,
    VCol,
    VHover,
    VIcon,
    VRow,
  },
  props: {
    /** HTML String*/
    highlights: { type: String },
    id: { type: String },
    value: { type: [String, Array, Number] as PropType<string | string[] | Alternative[] | number> },
    label: { type: String },
    hint: { type: String },
    icon: { type: String },
    loading: { type: Boolean },
    userMadeChange: { type: Boolean },
    useHtmlInChips: { type: Boolean },
    readonly: { type: Boolean, default: false },
    disableEdit: { type: Boolean, default: false },
    user: { type: Object as PropType<UserInfoDto>, },
    field: { type: Object as PropType<MdfFieldEntity> },
    truncate: { type: Boolean },
    alternativesColors: { type: Object as PropType<Record<string, string>> },
    valueWillRemainUnchangedText: { type: String, default: '' },
    isUpdatingMultipleItems: { type: Boolean },
    expandable: { type: Boolean },
    compact: { type: Boolean },
    clearable: { type: Boolean, default: false },
    /** Leaves out the icon column when there is no icon, as the field's own hideIconSpace does */
    hideIconSpace: { type: Boolean, default: false },
  },
  computed: {
    hasIconColumn(): boolean {
      return Boolean(this.icon) || !this.hideIconSpace;
    },
    ariaRole() {
      switch (this.field?.type) {
        case 'text':
        case 'number':
        case 'json':
          return 'textbox';
        case 'checkbox':
          return 'checkbox';
        case 'date':
        case 'choice':
        case 'multiplechoice':
        case 'user':
        case 'treechoice':
        default:
          return 'combobox';
        case 'link':
        case 'file':
          return 'link';
        case 'button':
          return 'button';
      }
    },
    proxyValue() : string | string[] | number | undefined {
      if (Array.isArray(this.value)) {
        // not using this type assertion here makes the following statement error out by ts 4.1.3
        return (this.value as Array<string | Alternative>).map(v => typeof v === 'string' ? v : v.label);
      } else {
        return this.value;
      }
    },
    shouldShowValueRemainUnchangedText(): boolean {
      return !!this.valueWillRemainUnchangedText && (!this.value || isEmpty(this.value)) && this.value !== null;
    },
    shouldRenderChips() : boolean {
      return Array.isArray(this.value);
    },
    madeChange() : boolean {
      return this.userMadeChange ?? false;
    },
    clearDisabledTooltip() : string {
      if (this.field?.required) {
        return 'Required field can not be cleared';
      }
      return '';
    },
    isExpandable() {
      if (this.expandable) {
        return true;
      }
      return this.field && !this.readonly && [FieldType.Choice, FieldType.MultipleChoice, FieldType.TreeChoice, FieldType.SubType].includes(this.field.type);
    },
    hasValue(): boolean {
      if (this.value === null || this.value === undefined) {
        return false;
      }
      if (typeof this.value === 'string' && this.value === '') {
        return false;
      }
      if (Array.isArray(this.value) && this.value.length === 0) {
        return false;
      }
      return true;
    },
    showClearIcon(): boolean {
      return this.clearable && this.hasValue && !this.readonly && !this.disableEdit;
    },
    choiceFieldStyle() {
      if (this.alternativesColors && typeof this.proxyValue === 'string' && this.alternativesColors[this.proxyValue]) {
        return `color:${this.alternativesColors[this.proxyValue]} !important`;
      }
      return '';
    }
  },
  methods: {
    getCommonChipStyle,
    doEdit() {
      if (!this.readonly) {
        this.$emit('clicked-edit');
      }
    },
    clearField() {
      if (!this.readonly) {
        this.$emit('clear-field');
      }
    },
    onRemoveClicked(index: number | string) {
      assert(typeof index === 'number', 'index should be a number');
      this.$emit('remove-value-clicked', index);
    },
    isFreeformInputError(input: string) {
      if ((this.field?.minCharacters && input.length < this.field.minCharacters)
        || (this.field?.maxCharacters && input.length > this.field.maxCharacters)) {
        return true;
      }
      return false;
    },
    getChipStyle(val: string) {
      if (this.alternativesColors && typeof val === 'string' && this.alternativesColors[val] && !this.isFreeformInputError(val)) {
        return this.getCommonChipStyle(this.alternativesColors[val]);
      }
      return '';
    },
  },
});

</script>

<template>
  <VHover v-slot="{ hover }">
    <VRow
      v-if="valueWillRemainUnchangedText && !value && value !== null && !madeChange"
      dense
      align="center"
      class="mdf-field-row flex-nowrap"
    >
      <VCol
        v-if="hasIconColumn"
        cols="auto"
        class="value-field-icon"
        style="cursor: initial; width: 28px;"
      >
        <VIcon
          small
          class="pl-1 mt-4"
        >{{ icon }}</VIcon>
      </VCol>
      <VCol
        v-tooltip="hint"
        class="mdf-value-field"
        style="cursor: initial"
      >
        <label class="ml-0">{{ label }}</label>
        <div
          class="mdf-value"
          style="font-style: italic"
          @click="doEdit"
        >{{ valueWillRemainUnchangedText }}</div>
      </VCol>
      <div
        v-if="(madeChange || value === null) && isUpdatingMultipleItems"
        class="value-field-icon pl-1"
        style="margin-left: 6px"
      >
        <VBtn
          v-tooltip="$t('MetaDataLayout.RevertChanges')"
          :disabled="readonly"
          :loading="loading"
          class="ma-0"
          icon
          small
          @click.stop="$emit('reset-field-value')"
        >
          <VIcon>mdi-restore</VIcon>
        </VBtn>
      </div>
      <VCol
        v-if="isUpdatingMultipleItems && !madeChange"
        cols="auto"
        class="value-field-icon pl-1"
        style="margin-left: 6px"
      >
        <div v-tooltip="readonly ? $t('MetaDataLayout.CannotBeCleared') : $t('MetaDataLayout.RemoveValue')">
          <VBtn
            :disabled="readonly"
            :loading="loading"
            class="mt-2"
            icon
            small
            @click.stop="clearField"
          >
            <VIcon>close</VIcon>
          </VBtn>
        </div>
      </VCol>
    </VRow>
    <div v-else>
      <label
        v-if="!compact"
        :class="[!hasIconColumn && 'ml-0']"
      >{{ label }}</label>
      <div :class="['value-field', hover && 'hovered', disableEdit && 'disabled']">
        <VIcon
          v-if="hasIconColumn"
          class="mt-1 mr-1 value-field-icon"
          size="16"
        >{{ icon }}</VIcon>
        <div
          v-tooltip="hint"
          :class="['value d-flex justify-space-between', readonly && 'readonly']"
          :role="ariaRole"
          :aria-label="label"
          @click="doEdit"
        >
          <template v-if="!shouldRenderChips">
            <div
              v-if="!highlights || madeChange"
              :class="[truncate && 'text-truncate']"
            >
              <AvatarPicture
                v-if="user"
                class="mr-2 mt-n1"
                :user="user"
                small
              />
              <span :style="choiceFieldStyle">{{ proxyValue }}</span>
            </div>
            <div
              v-else
              v-html="highlights"
            />
          </template>
          <template v-else>
            <div class="mdf-value common-chip">
              <VChip
                v-for="(val, i) in proxyValue"
                :key="i"
                x-small
                :style="alternativesColors && typeof val == 'string' && alternativesColors[val] && !isFreeformInputError(val) ?
                  getCommonChipStyle(alternativesColors[val]) : ''"
                :class="(typeof val == 'string' && isFreeformInputError(val)) ? 'freeform-error' : ''"
                :close="!disableEdit && !readonly"
                @click:close="onRemoveClicked(i)"
              >
                <span
                  v-if="useHtmlInChips"
                  v-html="val"
                />
                <span v-else>{{ val }}</span>
              </VChip>
            </div>
          </template>
          <div class="d-flex align-center flex-shrink-0">
            <VIcon
              v-if="showClearIcon && hover"
              class="clear-on-hover"
              style="margin-top:-6px; left:-4px;"
              @click.stop="clearField"
            >backspace</VIcon>
            <VIcon
              v-if="isExpandable"
              style="margin-top:-6px"
            >arrow_drop_down</VIcon>
          </div>
        </div>
        <div
          v-if="(madeChange || value === null) && isUpdatingMultipleItems"
          class="value-field-icon pl-1"
          style="margin-left: 6px; align-self: center;"
        >
          <VBtn
            v-tooltip="$t('MetaDataLayout.RevertChanges')"
            :disabled="readonly"
            :loading="loading"
            class="ma-0"
            icon
            small
            @click.stop="$emit('reset-field-value')"
          >
            <VIcon>mdi-restore</VIcon>
          </VBtn>
        </div>
      </div>
    </div>
  </VHover>
</template>


<style scoped lang="stylus">

/*VUE2*/ @import '../../../stylus/input.styl'
</style>

